import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { PageHead } from "./Shop";
import { aboutData, whyChooseUs, journeyData } from "../mock/content";
import useSeo from "../hooks/useSeo";

const About = () => {
  const { tr, ui } = useLang();
  const a = aboutData;
  useSeo({
    title: ui.nav.about,
    description: { en: "About HAAS Mosaic — founded in Antioch in 2014, we reinterpret ancient mosaic techniques through modern aesthetics and master craftsmanship.", tr: "HAAS Mosaic Hakkında — 2014'te Antakya'da kurulduk; antik mozaik tekniklerini modern estetik ve usta işçilikle yeniden yorumluyoruz." },
  });
  return (
    <div className="bg-[#f7f3ee]">
      <PageHead title={ui.nav.about} subtitle={tr(a.intro)} image="/mosaic/2025/08/about.jpg" />

      <section className="py-20 md:py-28">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-14 items-center">
          <div className="card-img-zoom overflow-hidden order-2 md:order-1">
            <img src={a.image} alt="HAAS Mosaic" className="w-full h-[520px] object-cover" />
          </div>
          <div className="order-1 md:order-2">
            <p className="text-[#a67c52] text-[12px] uppercase tracking-luxe mb-4">{tr(a.kicker)}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.1] text-[#211d18] mb-6">{tr(a.title)}</h2>
            {a.paragraphs.map((p, i) => (
              <p key={i} className="text-[#5a5348] text-[15px] leading-relaxed mb-5">{tr(p)}</p>
            ))}
            <ul className="space-y-3 mt-6">
              {a.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] text-[#3a352d]"><Check size={18} className="text-[#a67c52] mt-0.5 shrink-0" /> {tr(b)}</li>
              ))}
            </ul>
            <p className="font-serif text-2xl text-[#211d18] mt-8">{tr(a.closing)}</p>
          </div>
        </div>
      </section>

      {/* Our Mosaic Journey — timeline */}
      <section className="py-20 md:py-28 bg-[#efe7db]">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8">
          <div className="text-center mb-14 md:mb-20">
            <p className="text-[#a67c52] text-[12px] uppercase tracking-luxe mb-3">{tr(journeyData.kicker)}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#211d18]">{tr(journeyData.title)}</h2>
          </div>

          <div className="relative">
            {/* vertical line */}
            <div className="md:hidden absolute left-3 top-0 bottom-0 w-px bg-[#d8c7ad]" />
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-[#d8c7ad] -translate-x-1/2" />

            <div className="space-y-8 md:space-y-0">
              {journeyData.milestones.map((m, i) => {
                const left = i % 2 === 0;
                return (
                  <div key={m.year} className="relative md:grid md:grid-cols-2 md:mb-12">
                    {/* dot */}
                    <span className="absolute z-10 top-2 left-3 md:left-1/2 w-4 h-4 rounded-full bg-[#a67c52] ring-4 ring-[#efe7db] -translate-x-1/2" />
                    {/* card */}
                    <div className={`pl-10 md:pl-0 ${left ? "md:pr-12 md:text-right" : "md:col-start-2 md:pl-12"}`}>
                      <div className="bg-white shadow-sm hover:shadow-md transition-shadow p-6 md:inline-block md:max-w-md">
                        <span className="font-serif text-3xl md:text-4xl text-[#a67c52] leading-none">{m.year}</span>
                        <p className="text-[#5a5348] text-[14px] md:text-[15px] leading-relaxed mt-3 text-left">{tr(m.text)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="pb-24">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#a67c52] text-[12px] uppercase tracking-luxe mb-3">Unleashing Creativity, Delivering Excellence</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#211d18]">{tr({ en: "Why Should You Choose Us?", tr: "Neden Bizi Seçmelisiniz?" })}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {whyChooseUs.map((w, i) => (
              <div key={i} className="card-img-zoom group relative overflow-hidden h-[360px] bg-[#211d18]">
                <img src={w.image} alt={tr(w.title)} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <h3 className="text-white font-serif text-2xl mb-2">{tr(w.title)}</h3>
                  <p className="text-white/80 text-[13px] leading-relaxed">{tr(w.text)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="bg-[#211d18] text-white px-8 md:px-16 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-serif text-3xl md:text-4xl mb-2">{ui.ctaTitle}</h3>
              <p className="text-white/70 text-[15px]">{ui.ctaText}</p>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-3 bg-[#a67c52] hover:bg-[#8f6844] text-white text-[13px] uppercase tracking-[0.16em] px-8 py-4 transition-colors whitespace-nowrap">
              {ui.getInTouch} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
